import { atom, useAtom } from 'jotai'
import { IMemberData, Member } from '../../models/Member'
import { filterItems, IFilterData } from '../../utils/filterhelper'
import {
    IFilter,
    initialFilter,
    initialSelectedFilters,
} from '../../models/Filter'
import { membersAtom } from '../../api/members/members'
import {
    sortBy,
    sortNotesBy,
    sortProperty,
    sortUpdatesBy,
    sortWorkplacesBy,
} from '../../utils/sortHelper'
import { filterByValue } from '../../utils/searchHelper'
import { uniq } from '../../utils/utils'
import { Note } from '../../models/Note'
import { Workplace } from '../../models/Workplace'
import { UpdateBatch } from '../../models/UpdateBatch'

export interface FilterState {
    filter: IFilter
}

export type TypeResponseGetMembers = {
    Medlemmer: IMemberData[]
}

const initSort: sortProperty = {
    direction: 'ascending',
    property: 'name',
}

export type TypeResponse = TypeResponseGetMembers & IFilterData
const filterAtom = atom<IFilter>(initialFilter)
const filterSelected = atom(initialSelectedFilters)
const sortedAtom = atom<sortProperty>(initSort)
const sortLock = atom(false)
const searchAtom = atom<string>('')
const searchNoteAtom = atom<string>('')
const searchWorkplaceAtom = atom<string>('')
const searchMembersAtom = atom<Member[]>([])
const showOnlyFavorites = atom<boolean>(false)
const allNotes = atom<Note[]>([])
const allWorkplaces = atom<Workplace[]>([])
const allUpdates = atom<UpdateBatch[]>([])
const allUpdatesDone = atom<UpdateBatch[]>([])
const allUpdatesInProgress = atom<UpdateBatch[]>([])

const searchPageAtom = atom((get) => {
    const sorted = get(sortedAtom)
    const members = get(searchMembersAtom)
    let filteredItems = [...members]

    if (sorted) {
        filteredItems = sortBy([...filteredItems], sorted)
    }

    return [...filteredItems]
})

const workplacesPageAtom = atom((get) => {
    const sorted = get(sortedAtom)
    const workplaces = get(allWorkplaces)
    let sortedWorkplaces = [...workplaces]
    const search = get(searchWorkplaceAtom)

    if (sorted) {
        sortedWorkplaces = sortWorkplacesBy(sortedWorkplaces, sorted)
    }

    if (search) {
        sortedWorkplaces = filterByValue([...sortedWorkplaces], search, [
            'name',
            'address',
        ])
    }

    return sortedWorkplaces
})

const updatePageAtom = atom((get) => {
    const sorted = get(sortedAtom)
    const all = get(allUpdates)
    let sortedUpdates = [...all]
    if (sorted) {
        sortedUpdates = sortUpdatesBy(sortedUpdates, sorted)
    }

    return sortedUpdates
})

const updatePageDoneAtom = atom((get) => {
    const sorted = get(sortedAtom)
    const all = get(allUpdatesDone)
    let sortedUpdates = [...all]
    if (sorted) {
        sortedUpdates = sortUpdatesBy(sortedUpdates, sorted)
    }

    return sortedUpdates
})

const updatePageInProgressAtom = atom((get) => {
    const sorted = get(sortedAtom)
    const all = get(allUpdatesInProgress)
    let sortedUpdates = [...all]
    if (sorted) {
        sortedUpdates = sortUpdatesBy(sortedUpdates, sorted)
    }

    return sortedUpdates
})

const notePageAtom = atom((get) => {
    const sorted = get(sortedAtom)
    const notes = get(allNotes)
    let sortedNotes = [...notes]
    const search = get(searchNoteAtom)

    if (sorted) {
        sortedNotes = sortNotesBy(sortedNotes, sorted)
    }

    // search
    if (search) {
        sortedNotes = filterByValue([...sortedNotes], search, [
            'content',
            'title',
            'member',
        ])
    }

    return sortedNotes
})

// FilteredItems
const filteredAtom = atom((get) => {
    const selected = get(filterSelected)
    const members = get(membersAtom)
    const sorted = get(sortedAtom)
    const search = get(searchAtom)
    const favoritesOnly = get(showOnlyFavorites)
    let filteredItems = filterItems(selected, [...members.members])

    if (sorted) {
        filteredItems = sortBy([...filteredItems], sorted)
    }

    if (search) {
        filteredItems = filterByValue([...filteredItems], search, [
            'fullName',
            'workPlace',
            'birthDay',
            'age',
        ])
    }

    if (favoritesOnly) {
        filteredItems = [...filteredItems].filter((m) => m.isFavorite)
    }

    return [...uniq(filteredItems, 'memberId')]
})

export {
    filterAtom,
    filteredAtom,
    filterSelected,
    sortedAtom,
    searchAtom,
    searchPageAtom,
    searchMembersAtom,
    showOnlyFavorites,
    notePageAtom,
    allNotes,
    allWorkplaces,
    workplacesPageAtom,
    allUpdates,
    allUpdatesInProgress,
    allUpdatesDone,
    updatePageDoneAtom,
    updatePageInProgressAtom,
    updatePageAtom,
    sortLock,
    searchWorkplaceAtom,
    searchNoteAtom,
}
